import React, { forwardRef, Fragment, useEffect, useState } from 'react';
import { AppHeader, Container, Spinner_v2 as Spinner, Typography, TypographyVariantsEnum } from '@mindly/ui-components';
import { IonContent, IonIcon } from '@ionic/react';
import { checkmarkCircleOutline } from 'ionicons/icons';
import { ModalSuccessContainer, PaymentModalStyled } from './LoadPaymentModal.styled';
import SuccessPaymentModal from '../../pages/SubscriptionsPayment/SuccessPaymentModal/SuccessPaymentModal';
import { FormattedMessage } from 'react-intl';
import { SuccessTrialSessionWidget } from '../../widgets';
import { RouteComponentProps } from 'react-router';
import { useTranslation } from 'react-i18next';

type LoadPaymentModalProps = {
  fetchingStatus: null | 'start' | 'success';
  paymentStatus: null | 'confirmed' | 'paid' | 'unknown';
  loadType: 'payment' | 'subscription' | 'subscriptionBooking' | 'checkout' | 'neutral' | 'go_to_payment';
  isTrial?: boolean;
  history?: RouteComponentProps['history'];
  subscriptionData?: {
    subscriptionID: string | null;
    therapistID: string;
  };
};

const LoadPaymentModal = forwardRef<HTMLIonModalElement, LoadPaymentModalProps>(
  ({ fetchingStatus, paymentStatus, subscriptionData, loadType, isTrial = false, history }, ref) => {
    const [isTrialOpened, setIsTrialOpened] = useState(false);
    const [isPaymentOpened, setIsPaymentOpened] = useState(false);
    const { t } = useTranslation('app');

    useEffect(() => {
      if (isTrial && fetchingStatus === 'success' && !!fetchingStatus?.length) {
        setIsTrialOpened(true);
      } else if (!!fetchingStatus?.length) {
        setIsPaymentOpened(true);
      } else {
        setIsTrialOpened(false);
        setIsPaymentOpened(false);
      }
    }, [isTrial, fetchingStatus]);

    return (
      <>
        <PaymentModalStyled ref={ref} isOpen={isTrialOpened} animated={false} backdropDismiss={false}>
          <SuccessTrialSessionWidget history={history} />
        </PaymentModalStyled>
        <PaymentModalStyled ref={ref} isOpen={isPaymentOpened} animated={false} backdropDismiss={false}>
          {!['subscription', 'subscriptionBooking'].includes(loadType) && <AppHeader />}
          <IonContent>
            <Container>
              <ModalSuccessContainer
                alignItems={fetchingStatus === 'success' && loadType === 'subscription' ? 'start' : undefined}
              >
                {fetchingStatus === 'start' && (
                  <>
                    <Spinner size={64} className="!text-[#029cee]" />
                    {loadType === 'payment' || loadType === 'subscriptionBooking' ? (
                      <Typography variant={TypographyVariantsEnum.Title28Semi} className="mt-6">
                        <FormattedMessage id={'payment.wait.book'} defaultMessage={'Бронюємо сеанс'} />
                      </Typography>
                    ) : loadType === 'checkout' ? (
                      <Typography variant={TypographyVariantsEnum.Title28Semi} className="mt-6">
                        <FormattedMessage
                          id={'payment.wait.payment_request'}
                          defaultMessage={'Створюємо запит на оплату'}
                        />
                      </Typography>
                    ) : loadType === 'neutral' ? (
                      <Typography variant={TypographyVariantsEnum.Title28Semi} className="mt-6">
                        {t('load_payment_modal.neutral.title')}
                      </Typography>
                    ) : loadType === 'go_to_payment' ? (
                      <Typography variant={TypographyVariantsEnum.Title28Semi} className="mt-6">
                        {t('load_payment_modal.go_to_payment.title')}
                      </Typography>
                    ) : (
                      <Typography variant={TypographyVariantsEnum.Title28Semi} className="mt-6">
                        <FormattedMessage id={'payment.wait.title'} defaultMessage={'Триває оплата'} />
                      </Typography>
                    )}
                    <Typography className="mt-1" variant={TypographyVariantsEnum.Text14Regular} opacity={50}>
                      <FormattedMessage id={'payment.wait.paying_process'} defaultMessage={'Будь ласка, зачекайте'} />
                    </Typography>
                  </>
                )}
                {fetchingStatus === 'success' && subscriptionData && loadType === 'subscription' ? (
                  <SuccessPaymentModal therapistID={subscriptionData.therapistID} />
                ) : fetchingStatus === 'success' ? (
                  <>
                    <IonIcon icon={checkmarkCircleOutline} />
                    <div className="text">
                      {loadType === 'payment' || loadType === 'subscriptionBooking' ? (
                        <FormattedMessage id={'general.booked'} defaultMessage={'Сеанс заброньовано'} />
                      ) : (
                        <>
                          <FormattedMessage id={'general.thanks'} defaultMessage={'Дякуємо'} />!
                        </>
                      )}
                    </div>
                    <br />
                    {paymentStatus === 'unknown' && (
                      <>
                        <span style={{ textAlign: 'center', lineHeight: '25px' }}>
                          {loadType === 'subscription' ? (
                            <FormattedMessage
                              id={'subscription.add.late'}
                              defaultMessage={'Твоя підписка з’явиться в додатку протягом 5 хвилин.'}
                            />
                          ) : (
                            <FormattedMessage
                              id={'session.add.late'}
                              defaultMessage={'Твій сеанс з’явиться в додатку протягом 5 хвилин.'}
                            />
                          )}
                        </span>
                        <br />
                        <span style={{ textAlign: 'center', lineHeight: '25px' }}>
                          <FormattedMessage
                            id={'session.refresh'}
                            defaultMessage={'Щоб побачити сеанс, треба потягнути екран вниз, щоб його оновити.'}
                          />
                        </span>
                      </>
                    )}
                  </>
                ) : null}
              </ModalSuccessContainer>
            </Container>
          </IonContent>
        </PaymentModalStyled>
      </>
    );
  }
);

export default LoadPaymentModal;
